/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import _ from 'lodash';
import { RoutePath } from '../../../shared/enums/RoutePath';
import PaymentDialogState from './enum/PaymentDialogState';
import usePayment from '../../pages/usePayment';
import { callApi } from '../../../api/helpers/api.helpers';
import draftApi from '../../../api/draft.api';

export function usePaymentDialog(submission) {
  const history = useHistory();
  const location = useLocation();
  const [paymentDialogState, setPaymentDialogState] = useState(null);
  const { submitPaymentRequest, checkPaymentResult } = usePayment();

  useEffect(() => {
    const _search = qs.parse(location.search);
    if (history.action === 'POP' && _search?.state === PaymentDialogState.INITIALIZING.state) {
      history.go(-2);
      return;
    }
    if (!_.isEmpty(_search?.state)) {
      setPaymentDialogState(PaymentDialogState[_search?.state?.toUpperCase()]);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!_.isEmpty(paymentDialogState)) {
      switch (paymentDialogState.state) {
        case PaymentDialogState.PROCESSING.state:
          if (checkPaymentResult) {
            checkPaymentResult();
          }
          break;
        case PaymentDialogState.RETRY.state:
          submitPaymentRequest();
          break;
        default:
          break;
      }
    }
  }, [paymentDialogState]);

  const handleDownloadProofOfPayment = async () => {
    const paymentAttachments = submission?.attachments?.filter(attachment =>
      attachment?.fileName?.includes('payment-report.pdf'),
    );
    if (paymentAttachments?.length > 0) {
      const pdfAttachment = paymentAttachments[0];
      const blob = await callApi(draftApi.fetchAttachment)(
        submission.id,
        pdfAttachment.id,
        pdfAttachment.fileName,
        pdfAttachment.mimeType,
      );
      const fileSrc = URL.createObjectURL(blob);
      createDownloadHandler(fileSrc, pdfAttachment.fileName);
    }
  };

  const createDownloadHandler = (uri, fileName) => {
    const link = document.createElement('a');
    link.href = uri;
    link.setAttribute('download', `${fileName}`);
    link.dispatchEvent(new MouseEvent('click'));
  };

  const handlePaymentPreview = () => {
    const params = {
      procedure: submission?.procedure,
      submissionId: submission?.id,
      ...(submission?.initialSubmissionProcedure && {
        initialSubmissionProcedure: submission?.initialSubmissionProcedure,
      }),
    };
    history.push(RoutePath.generateRoutePath(RoutePath.PROCEDURE_PREVIEW_SUBMISSION, params));
  };

  const setPopupState = ({ state }) => {
    const params = new URLSearchParams({ state });
    history.push({ ...history.location, search: params.toString() });
  };

  const handleGoToOverview = () => {
    const params = {
      procedure: submission?.procedure,
      ...(submission?.initialSubmissionProcedure && {
        initialSubmissionProcedure: submission?.initialSubmissionProcedure,
      }),
    };
    history.push(RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, params));
  };

  const handleTryAgain = () => {
    submitPaymentRequest();
  };

  const handleChangeMethodOfPayment = () => {
    setPaymentDialogState(null);
    history.push({ ...history.location, search: undefined });
  };

  const handlePayLater = () => {
    handleChangeMethodOfPayment();
    handleGoToOverview();
  };

  return {
    paymentDialogState,
    handleDownloadProofOfPayment,
    handleGoToOverview,
    handlePaymentPreview,
    handleTryAgain,
    handleChangeMethodOfPayment,
    handlePayLater,
    setPopupState,
    setPaymentDialogState,
  };
}
